import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { formatManwon } from '../../../utils/formatKRW';
import { DividendYearlyDataPoint } from '../DividendPage.hooks';

export interface DividendChartProps {
  yearlyData: DividendYearlyDataPoint[];
  drip: boolean;
  baseDividendYield: number;
}

export function DividendChart({ yearlyData, drip, baseDividendYield }: DividendChartProps) {
  return (
    <>
      <p style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>
        자산 성장 시뮬레이션
      </p>
      <div style={{ width: '100%', height: 280, marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={yearlyData} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <defs>
              <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="principalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#9ca3af" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="year" tickFormatter={(v) => `${v}년`} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => `${(v / 100_000_000).toFixed(1)}억`} width={60} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(value: ValueType, name: NameType) => [formatManwon(value as number), name]}
              labelFormatter={(v) => `${v}년 후`}
            />
            <Area type="monotone" dataKey="totalValue" name="총 자산" stroke="#3b82f6" fill="url(#totalGrad)" />
            <Area type="monotone" dataKey="principal" name="투자 원금" stroke="#9ca3af" fill="url(#principalGrad)" strokeDasharray="4 2" />
            {!drip && (
              <Area
                type="monotone"
                dataKey="cumulativeNetDividend"
                name="누적 배당 수령액"
                stroke="#10b981"
                fill="none"
                strokeDasharray="4 2"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>
        연간 배당금 추이 (세후)
      </p>
      <div style={{ width: '100%', height: 200, marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={yearlyData} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="year" tickFormatter={(v) => `${v}년`} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => `${Math.round(v / 10000)}만`} width={56} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(value: ValueType, name: NameType) => [formatManwon(value as number), name]}
              labelFormatter={(v) => `${v}년 후 연간`}
            />
            <Bar
              dataKey="annualNetDividend"
              name="연간 세후 배당금"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
              label={{
                position: 'top',
                formatter: (v: number) => (v > 5_000_000 ? `${Math.round(v / 10000)}만` : ''),
                fontSize: 10,
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <p style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>
        투자 원금 대비 실질 배당률 (YOC) 성장
      </p>
      <div style={{ width: '100%', height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={yearlyData} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="year" tickFormatter={(v) => `${v}년`} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v: number) => `${v.toFixed(1)}%`} width={48} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(value: ValueType) => [`${Number(value).toFixed(2)}%`, 'YOC']}
              labelFormatter={(v) => `${v}년 후`}
            />
            <ReferenceLine
              y={baseDividendYield * 100}
              stroke="#9ca3af"
              strokeDasharray="4 2"
              label={{
                value: `현재 배당률 ${(baseDividendYield * 100).toFixed(1)}%`,
                position: 'right',
                fontSize: 10,
              }}
            />
            <Line dataKey="yoc" name="투자 원금 대비 배당률(YOC)" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}
